import { staffUsersApi } from '@/services/api/staff-users'
import { useAuthStore } from '@/stores/auth'
import type { StaffTourEntry, StaffTourState } from '@/types/staff-user'

export const VIEW_TOUR_DISABLE_AUTO_START_KEY = 'ticket-buddy:view-tour:disable-auto-start'

const STATE_VERSION = 1
const VIEW_TOUR_PREFIX = 'ticket-buddy:view-tour:'
let writeQueue = Promise.resolve()

function parseObject(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}
}

export function normalizeTourState(value: unknown): StaffTourState {
  const raw = parseObject(value)
  const seenRaw = parseObject(raw.seen)
  const seen: Record<string, StaffTourEntry> = {}
  for (const [key, entryRaw] of Object.entries(seenRaw)) {
    const entry = parseObject(entryRaw)
    const version = Number(entry.version)
    seen[key] = {
      version: Number.isFinite(version) && version > 0 ? Math.trunc(version) : 1,
      completedAt: typeof entry.completedAt === 'string' ? entry.completedAt : null,
      dismissedAt: typeof entry.dismissedAt === 'string' ? entry.dismissedAt : null,
      lastStartedAt: typeof entry.lastStartedAt === 'string' ? entry.lastStartedAt : null,
    }
  }
  return {
    version: STATE_VERSION,
    autoStartDisabled: raw.autoStartDisabled === true,
    seen,
  }
}

export function tourKeyFromLegacyStorageKey(storageKey: string): string {
  return String(storageKey || '').replace(/^ticket-buddy:view-tour:/, '')
}

export function tourVersionFromKey(key: string): number {
  const match = String(key || '').match(/:v(\d+)$/)
  const version = match ? Number(match[1]) : 1
  return Number.isFinite(version) && version > 0 ? Math.trunc(version) : 1
}

function currentState(): StaffTourState {
  return normalizeTourState(useAuthStore().user?.tourState)
}

function hasLocalStorageFlag(key: string): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(key) === '1'
  } catch {
    return false
  }
}

async function persistState(next: StaffTourState): Promise<void> {
  const authStore = useAuthStore()
  const userId = authStore.user?.id
  if (!userId) return
  writeQueue = writeQueue
    .catch(() => undefined)
    .then(async () => {
      const updated = await staffUsersApi.patch(userId, { tourState: next })
      authStore.mergeSessionUser(updated)
    })
  await writeQueue
}

function mergeSeenEntry(
  state: StaffTourState,
  tourKey: string,
  version: number,
  patch: Partial<StaffTourEntry>,
): StaffTourState {
  const prev = state.seen?.[tourKey] ?? {}
  return {
    ...state,
    version: STATE_VERSION,
    seen: {
      ...(state.seen ?? {}),
      [tourKey]: {
        ...prev,
        version: Math.max(Number(prev.version) || 0, version),
        ...patch,
      },
    },
  }
}

export async function migrateLegacyTourState(storageKey: string, tourKey: string, version: number): Promise<void> {
  const state = currentState()
  let next = state
  let changed = false

  if (hasLocalStorageFlag(VIEW_TOUR_DISABLE_AUTO_START_KEY) && state.autoStartDisabled !== true) {
    next = { ...next, autoStartDisabled: true }
    changed = true
  }

  const seen = state.seen?.[tourKey]
  const alreadySeen = (Number(seen?.version) || 0) >= version
  if (hasLocalStorageFlag(storageKey) && !alreadySeen) {
    next = mergeSeenEntry(next, tourKey, version, { dismissedAt: new Date().toISOString() })
    changed = true
  }

  if (changed) {
    await persistState(next)
  }
}

export function hasSeenTour(tourKey: string, version: number): boolean {
  const entry = currentState().seen?.[tourKey]
  return (Number(entry?.version) || 0) >= version
}

export function areTourAutoStartsDisabled(): boolean {
  return currentState().autoStartDisabled === true || hasLocalStorageFlag(VIEW_TOUR_DISABLE_AUTO_START_KEY)
}

export async function markTourSeen(
  tourKey: string,
  version: number,
  reason: 'completed' | 'dismissed' | 'started' = 'dismissed',
): Promise<void> {
  const now = new Date().toISOString()
  const patch: Partial<StaffTourEntry> = reason === 'started'
    ? { lastStartedAt: now }
    : reason === 'completed'
      ? { completedAt: now }
      : { dismissedAt: now }
  await persistState(mergeSeenEntry(currentState(), tourKey, version, patch))
}

export async function disableAllTourAutoStarts(tourKey?: string, version = 1): Promise<void> {
  const state = { ...currentState(), autoStartDisabled: true }
  const next = tourKey
    ? mergeSeenEntry(state, tourKey, version, { dismissedAt: new Date().toISOString() })
    : state
  await persistState(next)
}

export function migrateAllKnownLocalTourKeysIntoState(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()
  const keys: string[] = []
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i)
      if (!key || !key.startsWith(VIEW_TOUR_PREFIX) || key === VIEW_TOUR_DISABLE_AUTO_START_KEY) continue
      keys.push(key)
    }
  } catch {
    return Promise.resolve()
  }
  return keys.reduce(
    (prev, key) => prev.then(() => {
      const tourKey = tourKeyFromLegacyStorageKey(key)
      return migrateLegacyTourState(key, tourKey, tourVersionFromKey(tourKey))
    }),
    Promise.resolve(),
  )
}
